/* eslint-disable @typescript-eslint/no-non-null-assertion */

import AddIcon from '@material-ui/icons/AddCircleOutline';
import LogIcon from '@material-ui/icons/AssignmentOutlined';
import DeleteIcon from '@material-ui/icons/DeleteOutlined';
import FileCopyIcon from '@material-ui/icons/FileCopyOutlined';
import RestoreIcon from '@material-ui/icons/RestoreOutlined';
import UndoIcon from '@material-ui/icons/UndoOutlined';
import { delay, guid, isNullOrUndefined, isNullOrUndefinedOrEmpty, TimeSpan, UTC } from 'rewire-common';
import { freeze, observable } from 'rewire-core';
import { createColumn, createColumnsToggleMenuItems, createGrid, ICell, IColumn, IGrid, IGridOptions, IRowData } from 'rewire-grid';
import {
  ErrorSeverity,
  field,
  IActionMenuItem,
  IError,
  IModalState,
  IToggleMenuItem,
  SingleParameterBuiltInValidator,
  validator,
} from 'rewire-ui';
import { DeepPartial, IEmployee, IJob, IOccupation, IScheduleRuleContext, IShift, ITimecard, TimecardInput } from '../DataTypes';
import DataMutations from '../graphql/Mutations';
import { AdvancedAutoCompleteActionType, getAdvancedAutoCompleteOptions } from '../ui/AdvancedAutoComplete';
import { notifications } from '../ui/Notifications';
import {
  numericTimeToTimestampRange,
  reasonCategoryDataMapper,
  roundTo,
  timecardToTimecardInput,
  timestampRangeToNumericTime,
} from '../utility/DataConverters';
import { formatTime, localTimeZoneUTCNow } from '../utility/DateUtils';
import { getNameSearcher } from '../utility/Searchers';
import SelectedRowsWatcher from '../utility/SelectedRowsWatcher';
import addReasonDialogModel from './AddReasonDialogModel';
import infoPanelShiftSchedulingHeaderViewModel from './InfoPanelShiftSchedulingHeaderViewModel';
import infoPanelShiftViewModel, { IInfoPanelShiftViewModelChangeHandler, IShiftInfo } from './InfoPanelShiftViewModel';
import InfoPanelViewModelBase from './InfoPanelViewModelBase';
import scheduleModel from './ScheduleModel';
import { getScheduleRuleContext } from './ScheduleRuleDialogModel';
import SchedulingCandidateEmployeeGridModel from './SchedulingCandidateEmployeeGridModel';
import schedulingCandidatePhoneDialogModel from './SchedulingCandidatePhoneDialogModel';
import { IResponseInput } from './ResponsesLogViewModel';

interface IInfoPanelShiftSchedulingViewModelState extends IModalState {
  shiftGrid: IGrid;
  scheduleRuleContext?: IScheduleRuleContext;
  isLoading: boolean;
}

export class InfoPanelShiftSchedulingViewModel
  extends InfoPanelViewModelBase<IInfoPanelShiftSchedulingViewModelState>
  implements IInfoPanelShiftViewModelChangeHandler
{
  optionsActionMenuItems: IActionMenuItem[];
  selectedTimecardsWatcher: SelectedRowsWatcher;
  schedulingGridModel: SchedulingCandidateEmployeeGridModel;

  constructor() {
    super();

    this.state.shiftGrid = {} as IGrid;
    this.schedulingGridModel = new SchedulingCandidateEmployeeGridModel(
      undefined,
      infoPanelShiftSchedulingHeaderViewModel.showScheduleRuleDialog,
    );
    this.state.scheduleRuleContext = undefined;
    this.state.isLoading = false;

    this.optionsActionMenuItems = [
      {
        name: 'duplicateShift',
        title: 'Duplicate Shift',
        icon: FileCopyIcon,
        onClick: () => this.showDuplicateShiftDialog(),
        closeOnClick: true,
        disabled: () => this.viewOnly || !this.editing || this.isNew || this.hasChanges(),
        visible: () => scheduleModel.viewMode === 'Job',
      },
      {
        name: 'deleteShift',
        title: 'Delete Shift',
        icon: DeleteIcon,
        onClick: () => this.deleteShift(),
        closeOnClick: true,
        disabled: () => this.viewOnly || !this.editing || this.isNew,
      },
      {
        name: 'resetToDefault',
        title: 'Reset to Default',
        icon: UndoIcon,
        onClick: () => this.resetShiftToDefault(),
        closeOnClick: true,
        disabled: () => this.viewOnly,
        visible: () => scheduleModel.viewMode === 'Job',
      },
      {
        name: 'undoAbsence',
        title: 'Undo Absence',
        icon: RestoreIcon,
        onClick: () => this.undoAbsence(),
        closeOnClick: true,
        disabled: () => this.viewOnly || !this.shiftHasAbsenceReason || this.isNew,
      },
      {
        name: 'addComment',
        title: 'Add Comment',
        icon: AddIcon,
        onClick: () => this.addComment(),
        closeOnClick: true,
        disabled: () => this.viewOnly,
      },
      { name: 'auditLog', title: 'View Audit Log', icon: LogIcon, onClick: () => this.viewAuditLog(), closeOnClick: true },
    ];

    this.action('phone employee', this.openPhoneDialog, {
      type: 'submit',
      disabled: () =>
        this.viewOnly ||
        !this.selectedTimecards?.length ||
        this.shiftGrid.hasErrors?.() ||
        !this.jobId ||
        !this.selectedSchedulingEmployee,
    }).action('schedule employee', this.submit, {
      type: 'submit',
      disabled: () =>
        this.viewOnly ||
        !this.selectedTimecards?.length ||
        this.shiftGrid.hasErrors?.() ||
        !this.jobId ||
        !this.selectedSchedulingEmployee,
    });

    this.selectedTimecardsWatcher = new SelectedRowsWatcher();
  }

  reset() {
    this.selectedTimecardsWatcher.unwatch();
    freeze(() => {
      this.state.shiftGrid = {} as IGrid;
      this.state.scheduleRuleContext = undefined;
      this.schedulingGridModel.reset();
    });
  }

  load = async (initialLoad: boolean = false) => {
    this.state.isLoading = true;
    this.state.enable = false;
    this.state.shiftGrid = this.createShiftGrid();
    await infoPanelShiftSchedulingHeaderViewModel.load();
    await this.loadSchedulingGrid(initialLoad);

    this.selectedTimecardsWatcher.watch(this.shiftGrid, async () => {
      if (this.selectedTimecards?.length) {
        await (this.state.scheduleRuleContext = getScheduleRuleContext(this.selectedTimecards));
        infoPanelShiftSchedulingHeaderViewModel.setScheduleRuleWithDefault();
        await this.loadSchedulingGrid();
      } else {
        this.state.scheduleRuleContext = undefined;
        this.schedulingGridModel.reset();
      }
    });

    // select all rows of shift grid that do not have a reason category of "absence", by default
    const rowCells = this.shiftGrid.rows.reduce((prev, curr) => {
      return curr.cells['reason']?.value?.category !== 'absence' ? [...prev, ...Object.values(curr.cells)] : prev;
    }, [] as ICell[]);
    this.shiftGrid.selectCells(rowCells);

    this.state.enable = true;
    this.state.isLoading = false;
  };

  onCurrentInnerShiftChange() {
    this.load();
  }

  async loadSchedulingGrid(initialLoad?: boolean) {
    await this.schedulingGridModel.load(this.scheduleRuleContext, this.scheduleRuleQuery, this.earnedDate, initialLoad, true);
  }

  get shift(): IShift | undefined {
    return infoPanelShiftViewModel.shift;
  }

  get occupation(): IOccupation | undefined {
    return infoPanelShiftViewModel.occupation;
  }

  get employee(): IEmployee | undefined {
    return infoPanelShiftViewModel.employee;
  }

  get earnedDate(): UTC {
    return infoPanelShiftViewModel.earnedDate;
  }

  get scheduleRuleQuery(): string | undefined {
    return infoPanelShiftSchedulingHeaderViewModel.scheduleRuleQuery;
  }

  get scheduleRuleContext(): IScheduleRuleContext | undefined {
    return this.state.scheduleRuleContext;
  }

  get innerShifts(): IShiftInfo[] {
    return infoPanelShiftViewModel.innerShifts;
  }

  get details(): DeepPartial<ITimecard>[] | undefined {
    return infoPanelShiftViewModel.currentInnerShift?.details;
  }

  get job(): IJob | undefined {
    return infoPanelShiftViewModel.job;
  }

  get jobId(): string | undefined {
    return infoPanelShiftViewModel.jobId;
  }

  get jobGroupId(): string | undefined {
    return infoPanelShiftViewModel.jobGroupId;
  }

  get editing(): boolean {
    return infoPanelShiftViewModel.editing;
  }

  get isNew(): boolean {
    return infoPanelShiftViewModel.isNew;
  }

  get shiftGrid(): IGrid {
    return this.state.shiftGrid;
  }

  get isLoading(): boolean {
    return this.state.isLoading || this.isGridLoading;
  }

  get isGridLoading(): boolean {
    return this.schedulingGridModel.isLoading;
  }

  get selectedTimecards(): ITimecard[] | undefined {
    const rows = this.shiftGrid.selectedRows;
    return rows?.map((r) => r.data);
  }

  get selectedSchedulingEmployee(): IEmployee | undefined {
    const row = this.schedulingGridModel.selectedRows?.[0];
    return row && scheduleModel.getEmployeeFromRow(row);
  }

  get selectedSchedulingEmployeeHasPhone(): boolean {
    return (
      !isNullOrUndefinedOrEmpty(this.selectedSchedulingEmployee?.phone?.home) ||
      !isNullOrUndefinedOrEmpty(this.selectedSchedulingEmployee?.phone?.mobile) ||
      !isNullOrUndefinedOrEmpty(this.selectedSchedulingEmployee?.phone?.work)
    );
  }

  get hasSchedulingResults(): boolean {
    return this.schedulingGridModel.hasSchedulingResults;
  }

  get viewOnly(): boolean {
    return infoPanelShiftViewModel.viewOnly;
  }

  get shiftHasAbsenceReason(): boolean {
    return infoPanelShiftViewModel.shiftHasAbsenceReason;
  }

  hasChanges(): boolean {
    return infoPanelShiftViewModel.hasChanges();
  }

  showDuplicateShiftDialog() {
    infoPanelShiftViewModel.showDuplicateShiftDialog();
  }

  deleteShift() {
    infoPanelShiftViewModel.deleteShifts();
  }

  addComment() {
    infoPanelShiftViewModel.addComment();
  }

  resetShiftToDefault() {
    infoPanelShiftViewModel.resetShiftToDefault();
  }

  undoAbsence() {
    infoPanelShiftViewModel.undoAbsence();
  }

  viewAuditLog() {
    infoPanelShiftViewModel.viewAuditLog();
  }

  protected getShiftGridData(): ITimecard[] {
    return this.shiftGrid.get?.().map((rowData: IRowData) => rowData.data as ITimecard) || [];
  }

  createShiftGrid() {
    const reasonSchema = `
      id
      name
      code
      display
      other(fields: ["category"])
    `;
    const reasonFilter = {
      field: 'category',
      in: ['worked', 'absence', 'break'],
    };
    const overtimeReasonFilter = {
      field: 'category',
      eq: 'overtime',
    };
    const reasonSearcher = getNameSearcher('reasons', () => reasonFilter, reasonSchema, reasonCategoryDataMapper);
    const overtimeReasonSearcher = getNameSearcher('reasons', () => overtimeReasonFilter);
    const employeeSearcher = getNameSearcher('employees');
    const cols: IColumn[] = observable([]);

    const unitsDifferenceOfValidator = (value: number, ...otherValues: number[]): IError | undefined => {
      if (otherValues.some((v: number | undefined) => isNullOrUndefined(v))) {
        return undefined;
      }

      const difference = otherValues.reduce((totalValue: number, currValue: number) => totalValue - currValue);
      if (value.toFixed(4) !== difference.toFixed(4)) {
        return { text: 'must be the difference of End Time, Start Time', severity: ErrorSeverity.Error };
      }

      return undefined;
    };

    const unitsValidator = [
      'required' as SingleParameterBuiltInValidator,
      validator(unitsDifferenceOfValidator, field('end'), field('start')),
    ];
    const startValidator = validator('requiredWhenOthersAreNotNull', field('end'));
    const endValidator = [validator('requiredWhenOthersAreNotNull', field('start')), validator('>', field('start'))];
    const reasonValidator = 'required' as SingleParameterBuiltInValidator;

    const reasonAction: AdvancedAutoCompleteActionType = { action: this.showAddReasonDialog, label: 'Add New Reason' };

    cols.push(
      createColumn('start', 'Start Time', {
        width: '45px',
        canSort: false,
        visible: true,
        type: { type: 'time' },
        map: this.formatCellTime,
        validators: startValidator,
        onValueChange: this.onShiftGridStartChange,
      }),
    );
    cols.push(
      createColumn('end', 'End Time', {
        width: '45px',
        canSort: false,
        visible: true,
        type: { type: 'time' },
        map: this.formatCellTime,
        validators: endValidator,
        onValueChange: this.onShiftGridEndChange,
      }),
    );
    cols.push(
      createColumn('units', 'Units', {
        width: '35px',
        canSort: false,
        visible: true,
        type: { type: 'number', options: { decimals: 4, thousandSeparator: false, allowNegative: false } },
        align: 'left',
        validators: unitsValidator,
        onValueChange: this.onShiftGridUnitsChange,
      }),
    );
    cols.push(
      createColumn('reason', 'Reason', {
        width: '55px',
        canSort: false,
        visible: true,
        type: {
          type: 'auto-complete',
          options: {
            ...getAdvancedAutoCompleteOptions('Reasons', reasonAction, undefined, { openOnFocus: false }),
            ...reasonSearcher,
          },
        },
        validators: reasonValidator,
      }),
    );
    cols.push(
      createColumn('employee', 'Employee', {
        width: '65px',
        canSort: false,
        visible: true,
        type: { type: 'auto-complete', options: employeeSearcher },
      }),
    );
    cols.push(
      createColumn('overtimeReason', 'Overtime Reason', {
        width: '75px',
        canSort: false,
        visible: false,
        type: { type: 'auto-complete', options: overtimeReasonSearcher },
      }),
    );
    cols.push(createColumn('accountCode', 'Account Code', { width: '70px', canSort: false, visible: false, type: 'text' }));
    cols.push(
      createColumn('rate', 'Rate', {
        width: '45px',
        canSort: false,
        visible: false,
        type: { type: 'number', options: { decimals: 4, thousandSeparator: false, fixed: true, allowNegative: false } },
      }),
    );
    cols.push(
      createColumn('factor', 'Factor', {
        width: '45px',
        canSort: false,
        visible: false,
        type: { type: 'number', options: { decimals: 4, thousandSeparator: false, fixed: true } },
      }),
    );
    cols.push(
      createColumn('amount', 'Amount', {
        width: '45px',
        canSort: false,
        visible: false,
        type: { type: 'number', options: { decimals: 2, thousandSeparator: true, fixed: true } },
      }),
    );

    const rows: IRowData[] = [];

    if (this.details) {
      this.details.forEach((timecard) => {
        const r: IRowData = { id: timecard.id, data: { ...timecard } };
        const eDate = timecard.earnedDate || this.earnedDate;
        if (timecard.effective) {
          const { start, end } = timestampRangeToNumericTime(timecard.effective as any, eDate as any);
          r.data!['start'] = start;
          r.data!['end'] = end;
        }
        rows.push(r);
      });
    }

    // create the grid model
    const gridOptions: IGridOptions = {
      multiSelect: true,
      canSelectCellFn: undefined,
      headerRowHeight: 24,
      rowHeight: 31,
      optionsMenuFn: () => ({
        items: createColumnsToggleMenuItems(cols, ['start', 'end', 'overtimeReason', 'accountCode', 'rate', 'factor', 'amount'], {
          onItemClick: this.toggleShiftGridColumnVisibility,
        }),
      }),
      readOnly: true,
      rowKeybindPermissions: {
        insertRow: false,
        duplicateRow: false,
        deleteRow: false,
      },
      clearSelectionOnBlur: false,
    };

    const grid: IGrid = createGrid(rows, cols, gridOptions);
    return grid;
  }

  formatCellTime(value: number): string {
    if (isNullOrUndefined(value)) {
      return value;
    }
    const date = localTimeZoneUTCNow().startOfDay().add(value, TimeSpan.hours, 4);
    return formatTime(date);
  }

  toDisplayDateString(utc?: UTC): string {
    return infoPanelShiftViewModel.toDisplayDateString(utc);
  }

  getShiftIdx(idx: number) {
    return idx;
  }

  toggleShiftGridColumnVisibility(_item: IToggleMenuItem, column: IColumn) {
    column.visible = !column.visible;

    if (column.name === 'start') {
      const endColumn = column.grid.column('end');
      if (endColumn) endColumn.visible = !endColumn.visible;
    } else if (column.name === 'end') {
      const startColumn = column.grid.column('start');
      if (startColumn) startColumn.visible = !startColumn.visible;
    }
  }

  onShiftGridStartChange = (cell: ICell, _value: any) => {
    const { start, end, units } = cell.row.cells;
    if (!isNullOrUndefined(start.value)) {
      if (!isNullOrUndefined(end.value)) {
        units.value = Math.max(roundTo(end.value - start.value, 4), 0);
      } else if (!isNullOrUndefined(units.value)) {
        end.value = roundTo(start.value + units.value, 4);
      }
    }
  };

  onShiftGridEndChange = (cell: ICell, _value: any) => {
    const { start, end, units } = cell.row.cells;
    if (!isNullOrUndefined(end.value)) {
      if (!isNullOrUndefined(start.value)) {
        units.value = Math.max(roundTo(end.value - start.value, 4), 0);
      } else if (!isNullOrUndefined(units.value)) {
        start.value = roundTo(end.value - units.value, 4);
      }
    }
  };

  onShiftGridUnitsChange = (cell: ICell, _value: any) => {
    const { start, end, units } = cell.row.cells;
    if (!isNullOrUndefined(units.value)) {
      if (!isNullOrUndefined(start.value)) {
        end.value = roundTo(start.value + units.value, 4);
      } else if (!isNullOrUndefined(end.value)) {
        start.value = roundTo(end.value - units.value, 4);
      }
    }
  };

  showAddReasonDialog = () => {
    addReasonDialogModel.load();
  };

  undo(): void {}

  openPhoneDialog = () => {
    if (!this.selectedSchedulingEmployeeHasPhone) {
      notifications.error('No phone number available for the selected employee.');
      return false;
    }
    if (this.canSubmit()) {
      schedulingCandidatePhoneDialogModel.load(this.selectedTimecards, this.selectedSchedulingEmployee!, this.earnedDate);
    }
    return false;
  };

  canSubmit() {
    return !(
      this.viewOnly ||
      !this.selectedTimecards?.length ||
      this.shiftGrid.hasErrors?.() ||
      !this.jobId ||
      !this.selectedSchedulingEmployee
    );
  }

  submit = async (response?: IResponseInput) => {
    if (!this.canSubmit()) {
      return false;
    }

    const timecardInputs: TimecardInput[] = [];
    const timecardsScheduled: TimecardInput[] = [];
    const shiftGridData = this.getShiftGridData();
    shiftGridData.forEach((timecardData: any) => {
      const timecard = { ...timecardData };
      const earnedDate = timecardData.earnedDate || this.earnedDate;
      timecard.effective = numericTimeToTimestampRange(timecardData.start, timecardData.end, earnedDate);
      timecard.id = timecard.id || guid();
      timecard.earnedDate = earnedDate;
      timecard.createdDate = timecard.createdDate || UTC.now();
      const timecardInput = timecardToTimecardInput(timecard as ITimecard);
      timecardInput.groupId = timecardInput.groupId || scheduleModel.routeManager.groupId || this.jobGroupId;
      timecardInput.occupationId = timecardInput.occupationId || this.occupation?.id;
      timecardInput.jobId = timecardInput.jobId || this.jobId;
      if (this.selectedTimecards?.some((st) => st.id === timecardInput.id)) {
        timecardInput.employeeId = this.selectedSchedulingEmployee?.id;
        timecardInput.reasonId = response?.reasonId ?? timecardInput.reasonId ?? timecardData.reason?.id;
        if (timecardInput.employeeId) {
          timecardsScheduled.push(timecardInput);
        }
      }
      timecardInputs.push(timecardInput);
    });

    // get timecardInputs from other shifts, if any
    const otherShifts = this.innerShifts.filter((s, idx) => idx !== infoPanelShiftViewModel.currentInnerShiftIdx);
    const otherShiftTimecardInputs = otherShifts.flatMap((s) => s.details.map((d) => timecardToTimecardInput(d)));
    if (otherShiftTimecardInputs.length) {
      timecardInputs.push(...otherShiftTimecardInputs);
    }

    if (response?.type && timecardsScheduled.length > 0) {
      const timecard = timecardsScheduled[0];
      if (!timecard) return false;
      const units = timecardsScheduled.reduce((sum, tc) => sum + (tc.units || 0), 0);
      const responseInput = {
        units,
        effective: timecard.earnedDate.toDateString(),
        employeeId: timecard.employeeId!,
        occupationId: timecard.occupationId,
        groupId: timecard.groupId,
        templateId: timecard.templateId,
        generatorId: shiftGridData[0].job?.generator?.id,
        type: response.type,
        comment: response.comment,
      };
      const result = await DataMutations.insertResponses([responseInput]);
      if (!result) {
        notifications.error('Response failed to save');
        return false;
      }

      // if response is not "yes", do not schedule the employee
      if (response.type !== 'yes') {
        await delay(1000);
        await this.loadSchedulingGrid();
        return true;
      }
    }

    const result = await DataMutations.saveJobTimecards(timecardInputs);

    if (result) {
      notifications.success('Employee Scheduled');
      infoPanelShiftViewModel.submit();
      return false;
    }
    notifications.error('Employee Scheduling Failed');
    return false;
  };
}

const infoPanelShiftSchedulingViewModel = new InfoPanelShiftSchedulingViewModel();
export default infoPanelShiftSchedulingViewModel;
